import React, { useState } from 'react';
import { ShieldCheck, Award, GraduationCap, CheckCircle2, Sparkles, Send, Check } from 'lucide-react';
import { COURSES } from '../data/coursesData';
import { sendApplicationEmails } from '../utils/emailService';

export default function Hero({ onLeadSubmitted }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    course: 'msc-ds',
    city: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    sendApplicationEmails(formData);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      if (onLeadSubmitted) onLeadSubmitted(formData);
    }, 900);
  };

  return (
    <section id="hero" style={{
      background: 'radial-gradient(circle at 10% 20%, #0A192F 0%, #07131E 90%)',
      color: '#FFFFFF',
      padding: '4rem 0 5rem 0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Subtle Ambient Glow */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '800px',
        height: '450px',
        background: 'radial-gradient(circle, rgba(212, 175, 55, 0.12) 0%, rgba(15, 37, 55, 0) 70%)',
        pointerEvents: 'none',
        zIndex: 1
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: '3.5rem',
          alignItems: 'center'
        }}>

          {/* Left Column: Value Proposition & Copy */}
          <div>
            {/* Urgent Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'rgba(212, 175, 55, 0.12)',
              border: '1px solid rgba(212, 175, 55, 0.4)',
              padding: '0.45rem 1rem',
              borderRadius: '9999px',
              fontSize: '0.85rem',
              color: '#FEF08A',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              <Sparkles size={16} color="#D4AF37" className="animate-pulse" />
              <span>Admissions Open 2026 • UGC Entitled Degrees</span>
            </div>

            {/* Main Headline */}
            <h1 style={{
              fontSize: 'clamp(2.1rem, 4.5vw, 3.4rem)',
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.03em',
              marginBottom: '1.25rem',
              color: '#FFFFFF'
            }}>
              Empower Your Career with Online Degrees from <span style={{
                background: 'linear-gradient(135deg, #FEF08A 0%, #D4AF37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>CHRIST</span>
            </h1>

            {/* Subheadline */}
            <p style={{
              fontSize: '1.1rem',
              lineHeight: 1.6,
              color: '#CBD5E1',
              marginBottom: '2rem',
              maxWidth: '560px'
            }}>
              Earn a UGC Entitled, WES Recognized Bachelor's or Master's Degree from one of India's most prestigious NAAC 'A+' Grade Deemed Universities without leaving your job.
            </p>

            {/* Key Pillars Checklist */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '2.5rem' }}>
              {[
                "100% Online Learning with Flexible Proctored Exams",
                "Equivalent to Regular On-Campus University Degree",
                "1-on-1 Placement Assistance with 500+ Top Recruiters",
                "Zero Interest Monthly EMI Options Available"
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircle2 size={16} color="#10B981" />
                  </div>
                  <span style={{ fontSize: '0.98rem', color: '#E2E8F0', fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>

            {/* Trust Badges Strip */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ShieldCheck size={26} color="#D4AF37" />
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#FFFFFF' }}>NAAC 'A+' Grade</div>
                  <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>Highest Accreditation</div>
                </div>
              </div>

              <div style={{ height: '30px', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Award size={26} color="#38BDF8" />
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#FFFFFF' }}>Category-1</div>
                  <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>UGC Autonomy</div>
                </div>
              </div>

              <div style={{ height: '30px', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <GraduationCap size={26} color="#10B981" />
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#FFFFFF' }}>50+ Years</div>
                  <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>Academic Excellence</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Lead Capture Form */}
          <div>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              padding: '2.25rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.15)',
              color: '#0F172A'
            }}>
              <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                <span className="badge-gold" style={{ marginBottom: '0.5rem' }}>Limited Seats Remaining</span>
                <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#0F2537', marginTop: '0.35rem' }}>
                  Get Free Counseling & Syllabus
                </h3>
                <p style={{ fontSize: '0.88rem', color: '#64748B', marginTop: '0.2rem' }}>
                  Fill details below to download brochure and receive fee details.
                </p>
              </div>

              {submitted ? (
                <div style={{
                  padding: '2rem',
                  backgroundColor: '#F0FDF4',
                  borderRadius: '14px',
                  border: '1px solid #BBF7D0',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: '#10B981',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem auto'
                  }}>
                    <Check size={32} color="#FFFFFF" />
                  </div>
                  <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#065F46', marginBottom: '0.5rem' }}>
                    Application Enquiry Received!
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#166534', marginBottom: '1.5rem' }}>
                    Thank you <strong>{formData.fullName}</strong>. An Academic Counselor from Christ University will contact you within 15 minutes with program syllabus and fee structure.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-outline"
                    style={{ width: '100%' }}
                  >
                    Submit Another Enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      className="form-input"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        required
                        placeholder="name@email.com"
                        className="form-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="form-label">Mobile Number *</label>
                      <input
                        type="tel"
                        required
                        pattern="[0-9]{10}"
                        placeholder="10 digit number"
                        className="form-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Select Online Program *</label>
                    <select
                      className="form-input"
                      value={formData.course}
                      onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                      style={{ cursor: 'pointer' }}
                    >
                      {COURSES.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.name} ({course.type})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Current City / State *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Bangalore, Karnataka"
                      className="form-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                    style={{ width: '100%', padding: '0.95rem', fontSize: '1rem', marginTop: '0.5rem' }}
                  >
                    {isSubmitting ? (
                      <span>Connecting to SMS Gateway...</span>
                    ) : (
                      <>Verify OTP & Apply Now <Send size={18} /></>
                    )}
                  </button>

                  <p style={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', margin: 0 }}>
                    🔒 100% Privacy Protected. No spam guarantee.
                  </p>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
